import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearch } from "wouter";
import { motion } from "motion/react";
import { Button, Field, Input, Logo, rise, staggerParent } from "../ui/index.ts";
import { Bar } from "../landing/shapes.tsx";
import { LiveStatus } from "../landing/stages.tsx";
import { nextOf } from "../lib/next.ts";
import { AuthFailure, configured, logIn, logInWithGoogle, signUp } from "../lib/supabase.ts";

type Mode = "signup" | "login";
export type Problem = { field: "email" | "password" | "form"; text: string };

/** Codes that mean a reset link was used, expired, or opened where it can't finish. */
export const DEAD_LINK = new Set(["otp_expired", "flow_state_expired", "flow_state_not_found", "bad_code_verifier", "session_not_found", "access_denied"]);

const PROBLEMS: Record<string, Problem> = {
  user_already_exists: { field: "email", text: "That email already has an account. Log in instead." },
  email_exists: { field: "email", text: "That email already has an account. Log in instead." },
  email_address_invalid: { field: "email", text: "That doesn't look like an email." },
  validation_failed: { field: "email", text: "That doesn't look like an email." },
  invalid_credentials: { field: "password", text: "Wrong email or password." },
  weak_password: { field: "password", text: "Use at least 8 characters." },
  email_not_confirmed: { field: "form", text: "Confirm your email first. The link is in your inbox." },
  over_request_rate_limit: { field: "form", text: "Too many tries. Wait a minute." },
  over_email_send_rate_limit: { field: "form", text: "Too many emails sent. Wait a minute." },
  same_password: { field: "password", text: "Your new password must be different from your current one." },
  ...Object.fromEntries([...DEAD_LINK].map((code) => [code, { field: "form", text: "This link is invalid or has expired." } satisfies Problem])),
};

export function problemOf(e: unknown): Problem {
  if (e instanceof Error && e.message === "not_configured") return { field: "form", text: "Sign-in isn't set up on this server yet." };
  if (!(e instanceof AuthFailure)) return { field: "form", text: "Can't reach headcount. Check your connection." };
  if (e.status === 429) return PROBLEMS.over_request_rate_limit!;
  return PROBLEMS[e.code] ?? { field: "form", text: "That didn't work. Try again." };
}

/**
 * Email and password, Google as the second way in, and a link to the other mode, beside the brand panel on wide screens.
 * A safe `?next=` path carries through both ways in and the switch between modes; App opens it once signed in. Sign-up's
 * `?email=`, from a job alert email, fills in the email.
 */
export function Auth({ mode }: { mode: Mode }) {
  const search = useSearch();
  const next = nextOf(search);
  const careers = next?.startsWith("/careers") ?? false;
  const keep = next ? `?next=${encodeURIComponent(next)}` : "";
  const signup = mode === "signup";
  const [email, setEmail] = useState(() => (signup ? (new URLSearchParams(search).get("email") ?? "").trim().slice(0, 254) : ""));
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<Problem | null>(null);
  const [sending, setSending] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    const back = (e: PageTransitionEvent) => e.persisted && setLeaving(false);
    addEventListener("pageshow", back);
    return () => removeEventListener("pageshow", back);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    if (!email.trim()) return setProblem({ field: "email", text: "Enter your email." });
    if (!password) return setProblem({ field: "password", text: "Enter a password." });
    if (signup && password.length < 8) return setProblem({ field: "password", text: "Use at least 8 characters." });
    setSending(true);
    setProblem(null);
    try {
      if (signup) {
        const r = await signUp(email.trim(), password, next);
        if (r.confirm) setConfirm(true);
      } else await logIn(email.trim(), password);
    } catch (err) {
      setProblem(problemOf(err));
    }
    setSending(false);
  };

  const google = async () => {
    if (leaving) return;
    setLeaving(true);
    setProblem(null);
    try {
      await logInWithGoogle(next);
    } catch (err) {
      setProblem(problemOf(err));
      setLeaving(false);
    }
  };

  const err = (f: Problem["field"]) => (problem?.field === f ? problem.text : undefined);

  return (
    <AuthShell footer={careers ? undefined : "Nothing sends until you apply."}>
      {confirm ? (
        <motion.div variants={rise} className="flex flex-col gap-2">
          <h1 className="font-display text-[34px] leading-[1.08] text-ink">Check your email.</h1>
          <p className="text-[15px] text-muted">The link in it finishes your account.</p>
        </motion.div>
      ) : (
        <>
          <motion.div variants={rise} className="flex flex-col gap-2">
            <h1 className="font-display text-[34px] leading-[1.08] text-ink">{careers ? (signup ? "Create an account to manage your application." : "Log in to apply.") : signup ? "Put your job search on autopilot." : "Welcome back."}</h1>
            <p className="text-[15px] text-muted">
              {signup ? "Already have an account? " : "New to headcount? "}
              <Link href={(signup ? "/login" : "/signup") + keep} className="font-semibold text-primary-ink underline-offset-4 hover:underline">
                {signup ? "Log in" : "Create an account"}
              </Link>
            </p>
          </motion.div>
          <motion.div variants={rise} className="flex flex-col gap-5">
            <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
              <Field label="Email" error={err("email")}>
                <Input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={!email} />
              </Field>
              <Field label="Password" error={err("password")}>
                <Input
                  type="password"
                  autoComplete={signup ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus={!!email}
                />
              </Field>
              {!signup && (
                <Link
                  href="/forgot-password"
                  state={{ email: email.trim() }}
                  className="-my-1.5 self-end rounded-control py-1.5 text-[13px] font-semibold text-primary-ink underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              )}
              {problem?.field === "form" && (
                <p className="text-sm text-bad-ink" role="alert">
                  {problem.text}
                </p>
              )}
              <Button type="submit" size="lg" className="mt-1" loading={sending} disabled={!configured}>
                {signup ? "Create account" : "Log in"}
              </Button>
            </form>
            <div className="flex items-center gap-3 text-[13px] text-muted" aria-hidden>
              <span className="h-px flex-1 bg-line" />
              or
              <span className="h-px flex-1 bg-line" />
            </div>
            <Button variant="secondary" size="lg" onClick={google} loading={leaving} disabled={!configured} icon={<GoogleMark />}>
              Continue with Google
            </Button>
            {!configured && <p className="text-sm text-bad-ink">Sign-in isn't set up on this server yet.</p>}
          </motion.div>
        </>
      )}
    </AuthShell>
  );
}

/**
 * The auth screens' frame: the logo, a centered column whose children rise in one after another, an optional line at the
 * foot, and the brand panel on wide screens. A new `step` rises the column in again and leaves the panel be.
 */
export function AuthShell({ step, footer, children }: { step?: string; footer?: ReactNode; children: ReactNode }) {
  return (
    <main className="grid min-h-dvh bg-page lg:grid-cols-2">
      <div className="flex flex-col px-5 pb-8 pt-6 md:px-10">
        <Link href="/" aria-label="headcount home" className="self-start rounded-control">
          <Logo size={22} />
        </Link>
        <motion.div
          key={step}
          className="mx-auto flex w-full max-w-[400px] flex-1 flex-col justify-center gap-6 py-12"
          variants={staggerParent(0.06)}
          initial="hidden"
          animate="show"
        >
          {children}
        </motion.div>
        {footer ? <p className="text-center text-xs text-muted lg:text-left">{footer}</p> : null}
      </div>
      <aside className="sunset relative m-3 hidden flex-col justify-between gap-10 overflow-hidden rounded-xl p-12 text-on-primary lg:flex xl:p-16">
        <Bar tone="tint" delay={0.15} opacity={0.14} className="-right-10 top-[14%] h-[460px] w-[170px]" />
        <Bar tone="orange" flip delay={0.25} className="right-[150px] top-[30%] h-[220px] w-[80px]" />
        <p className="relative max-w-[13ch] font-display text-[48px] leading-[1.02] xl:text-[56px]">Apply to jobs on autopilot.</p>
        <div className="relative w-full max-w-[440px] self-end">
          <LiveStatus />
        </div>
      </aside>
    </main>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="size-[18px]" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
